import api from '../../api/client';
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Tabs,
  Descriptions,
  Statistic,
  Tag,
  Spin,
  Alert,
  Space,
  Typography,
  Divider,
  Tooltip,
  Badge,
} from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  FilePdfOutlined,
  BankOutlined,
  DollarOutlined,
  WalletOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import config from "../../config";
import { useFarmProfile } from "../../contexts/FarmProfileContext";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Helper functions for PDF generation (keep your existing logic)
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [41, 128, 185];
};

const primaryColor = hexToRgb("#279e6c");

const Payslip = () => {
  const currentUser = useSelector((state) => state.account.user);
  const [payslips, setPayslips] = useState([]);
  const [salarySummary, setSalarySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taxInfo, setTaxInfo] = useState(null);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState("history");
  const { profile } = useFarmProfile();

  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  useEffect(() => {
    const fetchPayslipData = async () => {
      try {
        setLoading(true);
        setError(null);

        const historyRes = await api.get(`/salary/history/${currentUser.id}`);
        const salaryData = historyRes.data;
        setPayslips(salaryData);

        if (salaryData.length > 0) {
          const latest = salaryData[0];
          setSalarySummary({
            period: latest.period,
            status: latest.status,
            grossAmount: latest.grossAmount,
            netAmount: latest.netAmount,
            personalRelief: latest.personalRelief || 0,
            deductions: latest.deductions,
            totalDeductions: latest.totalDeductions,
            taxablePay: latest.taxablePay,
            allowableDeductions: latest.allowableDeductions,
            advanceSalary: latest.advanceSalary || 0,
          });

          setTaxInfo({
            paye: latest.paye || 0,
            nhif: latest.shif || 0,
            nssf: latest.nssf || 0,
            personalRelief: latest.personalRelief || 0,
          });
        }
      } catch (err) {
        console.error("Error fetching payslip data:", err);
        setError("Failed to load payslip data. Please try again later.");
        toast.error("Failed to load payslip data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.id) {
      fetchPayslipData();
    }
  }, [currentUser.id, apiUrl]);

  const generatePayslipPDF = (payslip) => {
    // Keep your existing PDF generation logic
    const doc = new jsPDF();
    doc.setFont("helvetica");
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(0, 0, 210, 30, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text(profile.name, 105, 15, { align: "center" });
    doc.setFontSize(8);
    doc.text(profile.address1, 105, 22, { align: "center" });
    doc.text(`${profile.email} • ${profile.phone1}`, 105, 28, { align: "center" });
    
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 35, 210, 15, "F");
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(`${payslip.period} EMPLOYEE PAYSLIP`, 105, 43, { align: "center" });
    
    doc.autoTable({
      startY: 60,
      head: [["Name", "Employee ID", "Department", "Period"]],
      body: [[`${currentUser.firstname} ${currentUser.lastname}`, currentUser.id, currentUser.department || "Not specified", payslip.period]],
      theme: "grid",
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: 20, right: 20 },
    });
    
    // Earnings
    const earningsY = doc.lastAutoTable.finalY + 15;
    doc.text("EARNINGS", 20, earningsY);
    doc.autoTable({
      startY: earningsY + 5,
      head: [["Description", "Amount (KES)"]],
      body: [["Basic Salary", payslip.grossAmount?.toFixed(2) || "0.00"]],
      theme: "grid",
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: 20, right: 20 },
      foot: [["Total Earnings", payslip.grossAmount?.toFixed(2) || "0.00"]],
      footStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold" },
    });
    
    // Deductions
    const deductionsY = doc.lastAutoTable.finalY + 15;
    doc.text("DEDUCTIONS", 20, deductionsY);
    const deductionsData = [
      ["PAYE", payslip.deductions?.paye?.toFixed(2) || "0.00"],
      ["NSSF Tier I", payslip.deductions?.nssf_tier1?.toFixed(2) || "0.00"],
      ["NSSF Tier II", payslip.deductions?.nssf_tier2?.toFixed(2) || "0.00"],
      ["SHIF", payslip.deductions?.shif?.toFixed(2) || "0.00"],
      ["Housing Levy", payslip.deductions?.levy?.toFixed(2) || "0.00"],
      ["Advance Salary", payslip.advanceSalary?.toFixed(2) || "0.00"],
    ].filter((item) => parseFloat(item[1]) > 0);
    
    doc.autoTable({
      startY: deductionsY + 5,
      head: [["Description", "Amount (KES)"]],
      body: deductionsData,
      theme: "grid",
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: 20, right: 20 },
      foot: [["Total Deductions", payslip.totalDeductions?.toFixed(2) || "0.00"]],
      footStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold" },
    });
    
    // Summary
    const summaryY = doc.lastAutoTable.finalY + 15;
    doc.text("SUMMARY", 20, summaryY);
    doc.autoTable({
      startY: summaryY + 5,
      body: [
        ["Total Earnings", payslip.grossAmount?.toFixed(2) || "0.00"],
        ["Total Deductions", payslip.totalDeductions?.toFixed(2) || "0.00"],
        ["Net Pay", payslip.netAmount?.toFixed(2) || "0.00"],
      ],
      theme: "grid",
      margin: { left: 20, right: 20 },
      foot: [["", "", ""]],
    });
    
    // Net Pay Highlight
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.rect(20, doc.lastAutoTable.finalY - 10, 170, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(`NET PAY: KES ${payslip.netAmount?.toFixed(2) || "0.00"}`, 105, doc.lastAutoTable.finalY - 4, { align: "center" });
    
    // Footer
    const footerY = doc.lastAutoTable.finalY + 15;
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.text("This is a computer generated payslip and does not require a signature.", 105, footerY, { align: "center" });
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 105, footerY + 5, { align: "center" });
    
    doc.save(`payslip-${payslip.period}-${currentUser.id}.pdf`);
  };

  const handleDownloadPayslip = async (payslip) => {
    try {
      generatePayslipPDF(payslip);
      toast.success("Payslip downloaded successfully!");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Unable to download payslip.");
    }
  };

  const handleViewDetails = (payslip) => {
    setSelectedPayslip(payslip);
    setShowDetailsModal(true);
  };

  // Table columns
  const columns = [
    { title: "Pay Period", dataIndex: "period", key: "period", sorter: true },
    { title: "Gross Amount (KES)", dataIndex: "grossAmount", key: "grossAmount", render: (val) => val?.toLocaleString() || "0.00", sorter: true },
    { title: "Net Amount (KES)", dataIndex: "netAmount", key: "netAmount", render: (val) => val?.toLocaleString() || "0.00", sorter: true },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <Tag color={status === "Paid" ? "green" : "orange"}>{status}</Tag> },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Download PDF">
            <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownloadPayslip(record)} />
          </Tooltip>
          <Tooltip title="View Details">
            <Button icon={<EyeOutlined />} size="small" onClick={() => handleViewDetails(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" tip="Loading payslip data..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={<Button onClick={() => window.location.reload()}>Retry</Button>}
      />
    );
  }

  return (
    <>
      <Row gutter={[24, 24]}>
        {/* Left Column - Payslip History */}
        <Col xs={24} lg={16}>
          <Card title="Payslip History" bordered={false}>
            <Table
              columns={columns}
              dataSource={payslips}
              rowKey="id"
              pagination={{ pageSize: 5, showSizeChanger: true }}
              scroll={{ x: "max-content" }}
              size="small"
            />
          </Card>

          <Card title="Kenyan Tax Information" bordered={false} style={{ marginTop: 24 }}>
            {taxInfo ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="PAYE Rate" value="10-30%" prefix={<PercentageOutlined />} />
                </Col>
                <Col span={12}>
                  <Statistic title="Personal Relief" value={`KES ${taxInfo.personalRelief?.toFixed(2)}`} prefix={<WalletOutlined />} />
                </Col>
                <Col span={12} style={{ marginTop: 16 }}>
                  <Statistic title="SHIF Rate" value="Sliding Scale" prefix={<BankOutlined />} />
                </Col>
                <Col span={12} style={{ marginTop: 16 }}>
                  <Statistic title="NSSF Rate" value="6% (Max KES 1,080)" prefix={<DollarOutlined />} />
                </Col>
              </Row>
            ) : (
              <Text type="secondary">Tax information not available</Text>
            )}
            <Divider />
            <Button type="primary" block>
              View Tax Details
            </Button>
          </Card>
        </Col>

        {/* Right Column - Salary Summary */}
        <Col xs={24} lg={8}>
          {salarySummary ? (
            <Card title="Salary Summary" bordered={false}>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <Tag color={salarySummary.status === "Paid" ? "green" : "orange"}>{salarySummary.status}</Tag>
                <Title level={4}>{salarySummary.period}</Title>
              </div>

              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Gross Pay">
                  <Text strong>KES {salarySummary.grossAmount?.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Total Deductions">
                  <Text type="danger">- KES {salarySummary.totalDeductions?.toLocaleString()}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Net Pay">
                  <Text strong style={{ color: "#52c41a", fontSize: 18 }}>KES {salarySummary.netAmount?.toLocaleString()}</Text>
                </Descriptions.Item>
              </Descriptions>

              <Divider>Deductions Breakdown</Divider>

              {salarySummary.deductions?.paye > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text>PAYE</Text>
                  <Text type="danger">- KES {salarySummary.deductions.paye?.toLocaleString()}</Text>
                </div>
              )}
              {salarySummary.deductions?.nssf_tier1 > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text>NSSF Tier I</Text>
                  <Text type="danger">- KES {salarySummary.deductions.nssf_tier1?.toLocaleString()}</Text>
                </div>
              )}
              {salarySummary.deductions?.nssf_tier2 > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text>NSSF Tier II</Text>
                  <Text type="danger">- KES {salarySummary.deductions.nssf_tier2?.toLocaleString()}</Text>
                </div>
              )}
              {salarySummary.deductions?.shif > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text>SHIF</Text>
                  <Text type="danger">- KES {salarySummary.deductions.shif?.toLocaleString()}</Text>
                </div>
              )}
              {salarySummary.deductions?.levy > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text>Housing Levy</Text>
                  <Text type="danger">- KES {salarySummary.deductions.levy?.toLocaleString()}</Text>
                </div>
              )}
              {salarySummary.advanceSalary > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <Text strong>Advance Salary</Text>
                  <Text strong type="danger">- KES {salarySummary.advanceSalary?.toLocaleString()}</Text>
                </div>
              )}

              <Divider />
              <Button type="primary" block icon={<FilePdfOutlined />} onClick={() => handleDownloadPayslip(salarySummary)}>
                Download Payslip
              </Button>
            </Card>
          ) : (
            <Card title="Salary Summary" bordered={false}>
              <Text type="secondary">No salary data available</Text>
            </Card>
          )}
        </Col>
      </Row>

      {/* Payslip Details Modal */}
      <Modal
        title={`Payslip Details - ${selectedPayslip?.period}`}
        open={showDetailsModal}
        onCancel={() => setShowDetailsModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              handleDownloadPayslip(selectedPayslip);
              setShowDetailsModal(false);
            }}
          >
            Download PDF
          </Button>,
        ]}
      >
        {selectedPayslip && (
          <>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <Title level={4}>
                {currentUser.firstname} {currentUser.lastname}
              </Title>
              <Text type="secondary">Employee ID: {currentUser.id}</Text>
              <Divider />
              <Tag color={selectedPayslip.status === "Paid" ? "green" : "orange"}>Status: {selectedPayslip.status}</Tag>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Card title="Earnings" size="small">
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <Text>Basic Salary</Text>
                    <Text strong>{selectedPayslip.grossAmount?.toLocaleString()}</Text>
                  </div>
                  <Divider style={{ margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text strong>Total Earnings</Text>
                    <Text strong>{selectedPayslip.grossAmount?.toLocaleString()}</Text>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Deductions" size="small">
                  {selectedPayslip.deductions?.paye > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text>PAYE</Text>
                      <Text type="danger">- {selectedPayslip.deductions.paye?.toLocaleString()}</Text>
                    </div>
                  )}
                  {selectedPayslip.deductions?.nssf_tier1 > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text>NSSF Tier I</Text>
                      <Text type="danger">- {selectedPayslip.deductions.nssf_tier1?.toLocaleString()}</Text>
                    </div>
                  )}
                  {selectedPayslip.deductions?.nssf_tier2 > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text>NSSF Tier II</Text>
                      <Text type="danger">- {selectedPayslip.deductions.nssf_tier2?.toLocaleString()}</Text>
                    </div>
                  )}
                  {selectedPayslip.deductions?.shif > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text>SHIF</Text>
                      <Text type="danger">- {selectedPayslip.deductions.shif?.toLocaleString()}</Text>
                    </div>
                  )}
                  {selectedPayslip.deductions?.levy > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text>Housing Levy</Text>
                      <Text type="danger">- {selectedPayslip.deductions.levy?.toLocaleString()}</Text>
                    </div>
                  )}
                  {selectedPayslip.advanceSalary > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <Text strong>Advance Salary</Text>
                      <Text strong type="danger">- {selectedPayslip.advanceSalary?.toLocaleString()}</Text>
                    </div>
                  )}
                  <Divider style={{ margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text strong>Total Deductions</Text>
                    <Text strong type="danger">- {selectedPayslip.totalDeductions?.toLocaleString()}</Text>
                  </div>
                </Card>
              </Col>
            </Row>

            <Card style={{ marginTop: 16, backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}>
              <div style={{ textAlign: "center" }}>
                <Text strong style={{ fontSize: 18, color: "#52c41a" }}>
                  Net Pay: KES {selectedPayslip.netAmount?.toLocaleString()}
                </Text>
              </div>
            </Card>
          </>
        )}
      </Modal>
    </>
  );
};

export default Payslip;